import React, { useState, useEffect } from "react";
import { getMyProfile } from "../services/platformApi";
import EditProfileModal from "./EditProfileModal";
import "./ProfilePage.css";

/**
 * ProfilePage — shows the logged-in employee's own profile + details.
 * Place at: src/components/ProfilePage.js
 * Data comes from GET /user/profile (returns { success, data }).
 */

/* ── Inline SVG icons (pure inline vectors, no icon library) ── */
const Svg = {
  shield: (
    <svg viewBox="0 0 24 24"><path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" /></svg>
  ),
  check: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M9 12l2 2 4-4" /></svg>
  ),
  pencil: (
    <svg viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
  ),
  id: (
    <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 9h4M7 13h6" /></svg>
  ),
  building: (
    <svg viewBox="0 0 24 24"><path d="M3 21h18M5 21V7l7-4 7 4v14" /><path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01" /></svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
  ),
  hourglass: (
    <svg viewBox="0 0 24 24"><path d="M5 22h14M5 2h14M17 22v-4.2a2 2 0 00-.6-1.4L12 12l-4.4 4.4a2 2 0 00-.6 1.4V22M7 2v4.2a2 2 0 00.6 1.4L12 12l4.4-4.4A2 2 0 0017 6.2V2" /></svg>
  ),
  user: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" /></svg>
  ),
  briefcase: (
    <svg viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /></svg>
  ),
  home: (
    <svg viewBox="0 0 24 24"><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></svg>
  ),
};

/* dd Mon yyyy — exact date */
function fmtDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submittedMsg, setSubmittedMsg] = useState("");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getMyProfile();
      setProfile(res && res.data ? res.data : res);
    } catch (err) {
      setError(err.message || "Could not load your profile.");
    } finally {
      setLoading(false);
    }
  };

  const initials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
  };

  if (loading) {
    return (
      <div className="pf-state">
        <div className="pf-spinner" />
        <span>Loading your profile...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pf-state pf-state--error">
        <div className="pf-state__icon">!</div>
        <p>{error}</p>
        <button className="pf-btn" onClick={load}>Retry</button>
      </div>
    );
  }

  const p = profile || {};
  const isAdmin = (p.role || "").toUpperCase() === "ADMIN";
  const status = p.status || "";
  const addr = (a, c, s, pin) => {
    const line = [a, c, s].filter(Boolean).join(", ");
    return line ? (pin ? `${line} - ${pin}` : line) : "-";
  };

  return (
    <div className="pf">
      {/* Cover banner */}
      <div className="pf-cover" />

      {/* Head card with overlap avatar */}
      <div className="pf-head">
        <div
          className="pf-head__avatar"
          onClick={() => p.photoUrl && setPreview(true)}
          title={p.photoUrl ? "View photo" : ""}
        >
          {p.photoUrl ? <img src={p.photoUrl} alt={p.fullName} /> : <span>{initials(p.fullName)}</span>}
        </div>

        <div className="pf-head__row">
          <div className="pf-head__id">
            <h1>{p.fullName || "-"}</h1>
            <p className="pf-head__sub">
              {p.email}
              {p.employeeId ? ` · ${p.employeeId}` : ""}
            </p>
            <div className="pf-badges">
              {p.designation && (
                <span className="pf-badge pf-badge--role">
                  {Svg.shield} {p.designation}
                </span>
              )}
              {isAdmin && (
                <span className="pf-badge pf-badge--admin">
                  {Svg.shield} Admin
                </span>
              )}
              {status && (
                <span className={`pf-badge pf-badge--${status.toLowerCase()}`}>
                  {Svg.check} {status}
                </span>
              )}
            </div>
          </div>
          <button className="pf-edit-btn" onClick={() => setEditing(true)}>
            {Svg.pencil} Edit Profile
          </button>
        </div>
      </div>

      {submittedMsg && <div className="pf-submitted">{Svg.check} {submittedMsg}</div>}

      {/* Quick stats — icon top, LABEL, then VALUE */}
      <div className="pf-stats">
        <Stat icon={Svg.id} label="Emp ID" value={p.employeeId || "-"} />
        <Stat icon={Svg.building} label="Department" value={p.department || "-"} />
        <Stat icon={Svg.calendar} label="Joined" value={fmtDate(p.dateOfJoining)} />
        <Stat icon={Svg.hourglass} label="Tenure" value={p.tenure || "-"} />
      </div>

      {/* All detail cards visible */}
      <div className="pf-grid">
        <div className="pf-card">
          <h3 className="pf-card__title">
            <span className="pf-card__ic">{Svg.user}</span> Personal Details
          </h3>
          <div className="pf-fields">
            <PF label="Full Name" value={p.fullName} />
            <PF label="Company Email" value={p.email} full />
            <PF label="Personal Email" value={p.personalEmail} full />
            <PF label="Phone" value={p.phone ? `+91 ${p.phone}` : "-"} full />
            <PF label="Date of Birth" value={fmtDate(p.dateOfBirth)} />
            <PF label="Gender" value={p.gender} />
            <PF label="Blood Group" value={p.bloodGroup} highlight />
          </div>
        </div>

        <div className="pf-card">
          <h3 className="pf-card__title">
            <span className="pf-card__ic">{Svg.briefcase}</span> Work Details
          </h3>
          <div className="pf-fields">
            <PF label="Employee ID" value={p.employeeId} highlight />
            <PF label="Department" value={p.department} />
            <PF label="Designation" value={p.designation} />
            <PF label="Role" value={p.role} />
            <PF label="Date of Joining" value={fmtDate(p.dateOfJoining)} highlight />
            <PF label="Emergency Contact" value={p.emergencyContactName} />
            <PF label="Emergency Phone" value={p.emergencyContactPhone ? `+91 ${p.emergencyContactPhone}` : "-"} />
          </div>
        </div>

        <div className="pf-card pf-card--full">
          <h3 className="pf-card__title">
            <span className="pf-card__ic">{Svg.home}</span> Address
          </h3>
          <div className="pf-fields">
            <PF full label="Current Address"
                value={addr(p.currentAddress, p.currentCity, p.currentState, p.currentPincode)} />
            <PF full label="Permanent Address"
                value={addr(p.permanentAddress, p.permanentCity, p.permanentState, p.permanentPincode)} />
          </div>
        </div>
      </div>

      {/* Edit Profile modal */}
      {editing && (
        <EditProfileModal
          profile={p}
          onClose={() => setEditing(false)}
          onSubmitted={() => {
            setEditing(false);
            setSubmittedMsg("Your change request was submitted. Awaiting admin approval.");
          }}
        />
      )}

      {/* Photo preview modal */}
      {preview && p.photoUrl && (
        <div className="pf-modal" onClick={() => setPreview(false)}>
          <div className="pf-modal__box" onClick={(e) => e.stopPropagation()}>
            <button className="pf-modal__close" onClick={() => setPreview(false)}>×</button>
            <img src={p.photoUrl} alt="Profile" />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="pf-stat">
      <div className="pf-stat__ic">{icon}</div>
      <div className="pf-stat__label">{label}</div>
      <div className="pf-stat__val">{value}</div>
    </div>
  );
}

function PF({ label, value, full, highlight }) {
  return (
    <div className={`pf-field ${full ? "pf-field--full" : ""}`}>
      <span className="pf-field__label">{label}</span>
      <span className={`pf-field__value ${highlight ? "pf-field__value--hl" : ""}`} title={value || "-"}>{value || "-"}</span>
    </div>
  );
}

export default ProfilePage;
